import { createAsyncThunk } from '@reduxjs/toolkit';
import { getImagesUrls } from '../../utils/s3Utils';
import {
  getSettingEcommercesRequest,
  createSettingEcommerceRequest,
  updateSettingEcommerceRequest,
  deleteSettingEcommerceRequest,
  initMeliAuthRequest,
  initShopifyAuthRequest,
  getEcommerceWarehousesRequest,
} from './ecommercesApi';
import { ECOMMERCE_LOGO_KEYS, ECOMMERCE_LOGO_CACHE, getLogoKey } from './ecommercesConstants';
import { SHOPIFY_SLUG } from './ecommercesPlatforms';
import { rememberOauthStart } from './oauthReturn';
import { extractApiError } from '../../utils/errors';

/*
 * One thunk per function of the legacy `useEcommerceIntegrations` hook, with
 * the same requests, the same messages and the same refetch rules: creating and
 * updating reload the listing, deleting drops the row locally.
 *
 * What they resolve with grew for the detail page: a creation answers with the
 * record, so the page can move to its address, and the OAuth inits answer with
 * the id of the record they created along with the URL.
 */

const MELI_INIT_ERROR = 'Error al iniciar autorización con Mercado Libre';

/**
 * The seller's integrations, each with its logo. The logos travel along too:
 * the catalog of integrations to add is drawn with them.
 */
export const fetchEcommerceIntegrations = createAsyncThunk(
  'ecommerces/fetchIntegrations',
  async (_, { rejectWithValue }) => {
    try {
      const logos = await getImagesUrls(ECOMMERCE_LOGO_KEYS, ECOMMERCE_LOGO_CACHE);
      const { data } = await getSettingEcommercesRequest();
      const integrations = data.map((ecommerce) => ({
        ...ecommerce,
        image: logos[getLogoKey(ecommerce.name)] || null,
      }));
      return { integrations, logos };
    } catch (error) {
      console.error(`Error fetching integrations: ${error}`);
      return rejectWithValue('No se pudieron cargar las integraciones.');
    }
  }
);

export const fetchEcommerceWarehouses = createAsyncThunk(
  'ecommerces/fetchWarehouses',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getEcommerceWarehousesRequest();
      return data;
    } catch (error) {
      console.error(`No se pudo obtener las bodegas de la compañia: ${error}`);
      return rejectWithValue('No se pudo obtener las bodegas de la compañia');
    }
  }
);

/** Resolves with the created integration, as the backend answers it. */
export const createEcommerceIntegration = createAsyncThunk(
  'ecommerces/createIntegration',
  async (integrationData, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await createSettingEcommerceRequest(integrationData);
      await dispatch(fetchEcommerceIntegrations());
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Error al crear la integración.');
    }
  }
);

/**
 * @param {{id: number, data: object}} params
 */
export const updateEcommerceIntegration = createAsyncThunk(
  'ecommerces/updateIntegration',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      await updateSettingEcommerceRequest(id, data);
      await dispatch(fetchEcommerceIntegrations());
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Error al actualizar la integración.');
    }
  }
);

export const deleteEcommerceIntegration = createAsyncThunk(
  'ecommerces/deleteIntegration',
  async (id, { rejectWithValue }) => {
    try {
      await deleteSettingEcommerceRequest(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Error al eliminar la integración.');
    }
  }
);

/** The id `set_meli_oauth` and `shopify_start_oauth` answer next to the URL. */
const getCreatedId = (data) => data?.setting_ecommerce?.id ?? null;

/** Resolves with `{ url, integrationId }`: where to send the seller, and the record created. */
export const initMeliAuth = createAsyncThunk(
  'ecommerces/initMeliAuth',
  async (integrationData, { rejectWithValue }) => {
    try {
      const { data, status } = await initMeliAuthRequest(integrationData);
      if (status === 200 && data.url) return { url: data.url, integrationId: getCreatedId(data) };
      throw new Error('Respuesta inválida del servidor');
    } catch (error) {
      console.error(`Error al iniciar autorización con Mercado Libre: ${error}`);
      return rejectWithValue(MELI_INIT_ERROR);
    }
  }
);

/**
 * Resolves with `{ url, integrationId }`.
 *
 * @param {{shopUrl: string, credentials: {clientId: string, clientSecret: string}, warehouse: number}} params
 */
export const initShopifyAuth = createAsyncThunk(
  'ecommerces/initShopifyAuth',
  async ({ shopUrl, credentials = {}, warehouse }, { rejectWithValue }) => {
    try {
      const cleanShop = shopUrl
        .replace('https://', '')
        .replace('http://', '')
        .replace(/\/$/, '');

      const { data } = await initShopifyAuthRequest({
        shop: cleanShop,
        client_id: credentials.clientId,
        client_secret: credentials.clientSecret,
        warehouse,
      });

      if (data.url) return { url: data.url, integrationId: getCreatedId(data) };
      return rejectWithValue('El servidor no devolvió la URL de autorización.');
    } catch (error) {
      console.error(`Shopify Auth Error: ${error}`);
      return rejectWithValue(
        error.response?.data?.error ||
          'Error al conectar con Shopify. Verifica la URL y credenciales.'
      );
    }
  }
);

/**
 * Asks a connected Shopify store to authorize Ninja again, with the
 * credentials it already has, and sends the seller there: the stock sync needs
 * permissions that the stores connected before it never granted. With the
 * same shop the init updates the same integration, and its token stays until
 * the callback saves the new one. The way back lands on `returnTo`.
 *
 * @param {{integrationId: number, returnTo: string}} params
 */
export const reauthorizeShopify = createAsyncThunk(
  'ecommerces/reauthorizeShopify',
  async ({ integrationId, returnTo }, { rejectWithValue }) => {
    try {
      const { data: integrations } = await getSettingEcommercesRequest();
      const integration = (integrations || []).find((row) => String(row.id) === String(integrationId));
      if (!integration) return rejectWithValue('No encontramos la integración de esta tienda en tu cuenta.');

      const authentication = integration.configuration?.authentication || {};
      // Connected with a token of its own: there is no OAuth to repeat.
      if (!authentication.client_secret) {
        return rejectWithValue(
          'Esta tienda se conectó con un token propio: genera uno nuevo en Shopify con los permisos que faltan y guárdalo en Integración E-commerce.'
        );
      }

      const { data } = await initShopifyAuthRequest({
        shop: String(authentication.shop_url || '')
          .replace('https://', '')
          .replace('http://', '')
          .replace(/\/$/, ''),
        client_id: authentication.api_key,
        client_secret: authentication.client_secret,
        warehouse: integration.warehouse,
      });
      if (!data.url) return rejectWithValue('El servidor no devolvió la URL de autorización.');

      rememberOauthStart({ id: getCreatedId(data) ?? integration.id, platform: SHOPIFY_SLUG, returnTo });
      window.location.assign(data.url);
      return { url: data.url };
    } catch (error) {
      console.error(`Shopify reauthorization error: ${error}`);
      return rejectWithValue(extractApiError(error, 'No se pudo iniciar la autorización con Shopify.'));
    }
  }
);

/**
 * Starts over a Mercado Libre authorization that never came back.
 *
 * `set_meli_oauth` creates the record before sending the seller away, leaves it
 * "initialized" if they never return, and refuses a second attempt on that
 * warehouse. So the pending record goes first, then the authorization starts
 * again on the same warehouse.
 *
 * Resolves with `{ url, integrationId, removedId }`. Rejects with
 * `{ message, removed }`: when the new start fails, the old record is already
 * gone and the listing is reloaded to say so.
 *
 * @param {object} integration - The pending one.
 */
export const retryMeliAuth = createAsyncThunk(
  'ecommerces/retryMeliAuth',
  async (integration, { dispatch, rejectWithValue }) => {
    try {
      await deleteSettingEcommerceRequest(integration.id);
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.error || 'No se pudo eliminar la autorización pendiente.',
        removed: false,
      });
    }

    try {
      const { data, status } = await initMeliAuthRequest({
        name: integration.name,
        type: integration.type,
        warehouse: integration.warehouse,
      });
      if (status === 200 && data.url) {
        return { url: data.url, integrationId: getCreatedId(data), removedId: integration.id };
      }
      throw new Error('Respuesta inválida del servidor');
    } catch (error) {
      console.error(`Error al reintentar la autorización con Mercado Libre: ${error}`);
      await dispatch(fetchEcommerceIntegrations());
      return rejectWithValue({ message: MELI_INIT_ERROR, removed: true });
    }
  }
);
